import { needsMowing } from '../sim/grid';
import type { ToolSpec } from '../sim/tools';
import { TILE_BY_ID, TILES, type TileId } from './tiles';

export type ToolId =
  'bikeHorn' | 'rake' | 'stringTrimmer' | 'sprinklerKey' | 'hedgeTrimmer' | 'shovel' | 'pruningSaw';

/**
 * Handheld tools (docs/GDD.md §5). Every tool is family-friendly: critters are shooed, never
 * harmed. Lawns never require a tool; tools make them easier.
 */
export interface ToolDef {
  id: ToolId;
  name: string;
  /** Short label for the in-game toolbar. */
  short: string;
  description: string;
  /** Tiles it clears (turned into ground) in the lane ahead. */
  clears: readonly TileId[];
  /** Whether it shoos nearby critters. */
  shoos: boolean;
  /** Fills molehills in the lane ahead. */
  flattens?: boolean;
  /** Trims grass (the string trimmer). */
  trims?: boolean;
  /** Works on every tile within reach all round the mower, not the lane ahead. */
  around?: boolean;
  /** Turns off sprinklers within reach (the sprinkler key). */
  shutsOff?: boolean;
  /** Sends critters home off the lawn for this many ticks (the bike horn). */
  sendsHome?: number;
  /** How many tiles it reaches. */
  range: number;
  charges: number;
  cooldownTicks: number;
  /** Campaign level that must be cleared before it can be bought. */
  unlockLevel: number;
  price: number;
  /** Implemented and on sale. Later tools need per-tile tags the sim doesn't have yet. */
  available: boolean;
  /** Its upgrade levels, in order (economy pass 2, owner 2026-10-05). */
  upgrades: readonly ToolUpgrade[];
}

/** One level of a tool upgrade: what it adds, its price, and when it opens. */
export interface ToolUpgrade {
  /** What it does, in a few words for the garage ("+1 charge"). */
  label: string;
  price: number;
  /** The campaign lawn that must be cleared before it is on sale. */
  opensAfter: number;
  /** Charges and tiles of reach added. */
  charges?: number;
  range?: number;
  /** The bike horn: critters stay away this many ticks instead. */
  sendsHome?: number;
}

export const TOOLS: readonly ToolDef[] = [
  {
    // Critters already scurry from the mower; the horn clears the lawn of them for a while.
    id: 'bikeHorn',
    name: 'Bike Horn',
    short: 'HORN',
    description: 'Sends every critter home for 15s.',
    clears: [],
    shoos: false,
    sendsHome: 900,
    range: 1,
    charges: 2,
    cooldownTicks: 300,
    unlockLevel: 8,
    price: 60,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 90, opensAfter: 40, charges: 1 },
      { label: 'Away 25s', price: 300, opensAfter: 80, sendsHome: 1500 },
    ],
  },
  {
    id: 'rake',
    name: 'Rake',
    short: 'RAKE',
    description: 'Clears sticks right in front of you.',
    clears: ['sticks'],
    shoos: false,
    range: 1,
    charges: 3,
    cooldownTicks: 60,
    unlockLevel: 15,
    price: 80,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 120, opensAfter: 40, charges: 1 },
      { label: 'Reach 2', price: 300, opensAfter: 80, range: 1 },
    ],
  },
  {
    // Cuts grass the mower can't (or shouldn't) drive to: hemmed in by flowers, in a tight corner.
    id: 'stringTrimmer',
    name: 'String Trimmer',
    short: 'TRIM',
    // Owner, 2026-10-10: it cuts all round the mower, and its Reach upgrade takes that 2 tiles out.
    description: 'Cuts the grass all round you.',
    clears: [],
    shoos: false,
    trims: true,
    around: true,
    range: 1,
    charges: 3,
    cooldownTicks: 60,
    unlockLevel: 25,
    price: 200,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 250, opensAfter: 40, charges: 1 },
      { label: 'Reach 2', price: 450, opensAfter: 80, range: 1 },
    ],
  },
  {
    id: 'sprinklerKey',
    name: 'Sprinkler Key',
    short: 'KEY',
    description: 'Shuts off sprinklers within 2 tiles.',
    clears: [],
    shoos: false,
    shutsOff: true,
    range: 2,
    charges: 2,
    cooldownTicks: 60,
    unlockLevel: 33,
    price: 120,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 180, opensAfter: 40, charges: 1 },
      { label: 'Reach 3', price: 400, opensAfter: 80, range: 1 },
    ],
  },
  {
    id: 'hedgeTrimmer',
    name: 'Hedge Trimmer',
    short: 'HEDGE',
    description: 'Trims away a shrub or hedge ahead.',
    clears: ['shrub', 'hedge', 'topiary'],
    shoos: false,
    range: 1,
    charges: 2,
    cooldownTicks: 120,
    unlockLevel: 43,
    price: 250,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 300, opensAfter: 60, charges: 1 },
      { label: 'Reach 2', price: 500, opensAfter: 100, range: 1 },
    ],
  },
  {
    id: 'shovel',
    name: 'Shovel',
    short: 'DIG',
    description: 'Digs out a rock or molehill ahead.',
    clears: ['rock'],
    shoos: false,
    flattens: true,
    range: 2,
    charges: 2,
    cooldownTicks: 240,
    unlockLevel: 40,
    price: 300,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 350, opensAfter: 60, charges: 1 },
      { label: 'Reach 2', price: 550, opensAfter: 100, range: 1 },
    ],
  },
  {
    // The Golf Course is lined with trees: one can go.
    id: 'pruningSaw',
    name: 'Pruning Saw',
    short: 'SAW',
    description: 'Fells a tree ahead, leaving a stump.',
    clears: ['tree'],
    shoos: false,
    range: 1,
    charges: 1,
    cooldownTicks: 300,
    unlockLevel: 61,
    price: 400,
    available: true,
    upgrades: [
      { label: '+1 charge', price: 450, opensAfter: 80, charges: 1 },
      { label: 'Reach 2', price: 700, opensAfter: 120, range: 1 },
    ],
  },
];

export const TOOL_BY_ID: Readonly<Record<ToolId, ToolDef>> = Object.fromEntries(
  TOOLS.map((t) => [t.id, t]),
) as Record<ToolId, ToolDef>;

/**
 * The sim's view of a tool. The sim clears by tile kind, so a tool may only list tiles whose kind
 * isn't shared with anything it shouldn't clear (a shovel must not clear every obstacle).
 */
export function simTool(def: ToolDef, art?: readonly TileId[]): ToolSpec {
  const kinds = [...new Set(def.clears.map((id) => TILE_BY_ID[id].kind))];
  let only: number[] | undefined;
  for (const kind of kinds) {
    if (needsMowing(kind)) throw new Error(`${def.id}: can't clear grass`);
    const sharedWith = TILES.filter((t) => t.kind === kind && !def.clears.includes(t.id));
    if (sharedWith.length === 0) continue;
    // A kind shared with other tiles: limit the tool to this lawn's tiles of the listed art.
    if (!art) {
      throw new Error(
        `${def.id}: can't clear ${def.clears.join('/')} without also clearing ${sharedWith.map((t) => t.id).join('/')} (pass the lawn's art)`,
      );
    }
    only = art.flatMap((id, i) => (def.clears.includes(id) ? [i] : []));
  }
  return {
    id: def.id,
    charges: def.charges,
    cooldownTicks: def.cooldownTicks,
    range: def.range,
    shoos: def.shoos,
    clears: kinds,
    ...(only ? { only } : {}),
    ...(def.flattens ? { flattens: true } : {}),
    ...(def.trims ? { trims: true } : {}),
    ...(def.around ? { around: true } : {}),
    ...(def.shutsOff ? { shutsOff: true } : {}),
    ...(def.sendsHome ? { sendsHome: def.sendsHome } : {}),
  };
}
